/**
 * Eigene Autopilot-API unter /api/v1 (docs/autopilot/03 „Eigene API“, 08 §7).
 * Gemeinsamer Vertrag zwischen apps/autopilot-api und apps/autopilot-web.
 */
import { z } from 'zod';
import { ActorType, AktiverStatus, Assignment, AuthorizationType, IsoDateTime, Score, Trigger, UsageTotals, Uuid, VorgangStatus, INTERVAL_MAX, INTERVAL_MIN, INTERVAL_STEP, } from './common.js';
import { Completeness, ContextIssue, ExclusionHit, SelectionCounts, Source } from './nidus-integration.js';
export const API_PREFIX = '/api/v1';
export const Role = z.enum(['Viewer', 'Operator', 'Admin']);
/** Entra-App-Rollenwerte (Token-Claim `roles`). */
export const ENTRA_ROLE_VALUES = {
    Viewer: 'Autopilot.Viewer',
    Operator: 'Autopilot.Operator',
    Admin: 'Autopilot.Admin',
};
// ---------------------------------------------------------------------------
// Zustände (03 „Zustände und Nebenläufigkeit“)
// ---------------------------------------------------------------------------
export const BatchPhase = z.enum([
    'DRAFT',
    'SELECTING',
    'PREVIEW_READY',
    'PREHEATING',
    'REVIEW_READY',
    'EXECUTING',
    'COMPLETED',
    'PAUSED',
    'CANCELLED',
    'FAILED',
    'NEEDS_ATTENTION',
]);
export const BatchCloseReason = z.enum([
    'EMPTY_SELECTION',
    'NO_ELIGIBLE_TICKETS',
    'POLICY_CHANGED',
    'AUTOMATION_DISABLED',
    'BUDGET_EXCEEDED',
    'TIME_LIMIT',
    'STOPPED_BY_USER',
    'SELECTION_EXPIRED',
    'SELECTION_FAILED',
    'EXECUTION_DISABLED',
    'COMPLETED',
]);
export const AnalysisState = z.enum([
    'PENDING',
    'CONTEXT_LOADING',
    'GENERATING',
    'EVALUATING',
    'READY',
    'NEEDS_REVIEW',
    'EXCLUDED',
    'CONFLICT',
    'FAILED',
]);
export const ExecutionItemState = z.enum([
    'QUEUED',
    'SUBMITTING',
    'PENDING_DELIVERY',
    'SUCCEEDED',
    'CONFLICT',
    'FAILED',
    'UNKNOWN',
    'SENT_NOT_RESOLVED',
    'CANCELLED',
]);
/**
 * Disjunkte primäre Ergebniskategorie je Ticket (02 „Ergebnis und Historie“).
 * Reihenfolge = Priorität bei mehreren Gründen (erste zutreffende gewinnt).
 */
export const RESULT_CATEGORIES = [
    'SUCCEEDED',
    'DELIVERY_UNKNOWN',
    'SENT_NOT_RESOLVED',
    'FAILED',
    'CONFLICT',
    'NEGATIVE_LIST',
    'CONTEXT_INCOMPLETE',
    'MANUALLY_EXCLUDED',
    'BELOW_THRESHOLD',
    'PENDING',
];
export const ResultCategory = z.enum(RESULT_CATEGORIES);
export const ResolutionKind = z.enum(['FINAL_ANSWER', 'NEEDS_INFORMATION', 'REQUIRES_ACTION', 'NOT_ANSWERABLE']);
// ---------------------------------------------------------------------------
// Session / Kataloge
// ---------------------------------------------------------------------------
export const Me = z.object({
    user: z.object({ id: Uuid, name: z.string(), email: z.string().nullable() }),
    roles: z.array(Role),
    gesellschaftIds: z.array(Uuid),
    installation: z.object({ name: z.string(), nidusInstanceId: z.string(), nidusBaseUrl: z.string() }),
    executionEnabled: z.boolean(),
});
export const ApiCatalogs = z.object({
    gesellschaften: z.array(z.object({ id: Uuid, name: z.string() })),
    vorgangstypen: z.array(z.object({ id: Uuid, name: z.string(), code: z.string() })),
    aktiveStatus: z.array(AktiverStatus),
    /** Ergänzung Backend: aktive Generatormodelle (Schlüssel hinterlegt) für Operatoren. */
    generatorModels: z
        .array(z.object({ id: Uuid, name: z.string(), provider: z.string(), modelId: z.string(), version: z.number().int(), active: z.boolean(), roles: z.array(z.string()) }))
        .optional(),
    /** Ergänzung Backend: aktuell administriertes Prüfprofil. */
    reviewProfile: z.object({ id: Uuid, version: z.number().int(), name: z.string() }).nullable().optional(),
});
// ---------------------------------------------------------------------------
// Batch
// ---------------------------------------------------------------------------
/** UI-Filter: Datumsangaben als Kalendertage Europe/Berlin (inklusiv), Backend rechnet in UTC um. */
export const BatchFilter = z.object({
    gesellschaftIds: z.array(Uuid).default([]),
    vorgangstypIds: z.array(Uuid).default([]),
    includeUntyped: z.boolean().default(false),
    statuses: z.array(AktiverStatus).default([]),
    createdFromDate: z.iso.date().nullable().default(null),
    createdToDate: z.iso.date().nullable().default(null),
    assignment: Assignment.default('ALL'),
});
export const CreateBatchRequest = z.object({ name: z.string().max(120).optional(), filter: BatchFilter });
export const ModelRef = z.object({
    modelConfigId: Uuid,
    modelConfigVersion: z.number().int(),
    name: z.string(),
    provider: z.string(),
    modelId: z.string(),
});
export const BatchProgress = z.object({
    total: z.number().int(),
    done: z.number().int(),
    failed: z.number().int(),
    inProgress: z.number().int(),
});
export const ResultCounts = z.record(ResultCategory, z.number().int());
export const BatchSummary = z.object({
    id: Uuid,
    name: z.string().nullable(),
    trigger: Trigger,
    actorType: ActorType,
    authorizationType: AuthorizationType.nullable(),
    createdBy: z.object({ id: Uuid, name: z.string() }).nullable(),
    gesellschaftIds: z.array(Uuid),
    policyVersion: z.number().int().nullable(),
    scheduledFor: IsoDateTime.nullable(),
    phase: BatchPhase,
    closeReason: BatchCloseReason.nullable(),
    generator: ModelRef.nullable(),
    ticketCount: z.number().int(),
    resolvedCount: z.number().int(),
    usage: UsageTotals,
    reusedAnalysisCount: z.number().int(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    finishedAt: IsoDateTime.nullable(),
    rowVersion: z.number().int(),
    /** Ergänzung Backend: Filterzusammenfassung für die Historie. */
    filter: z.lazy(() => BatchFilter).optional(),
});
export const BatchDetail = BatchSummary.extend({
    filter: BatchFilter,
    selection: z
        .object({
        nidusSelectionId: Uuid.nullable(),
        status: z.enum(['PENDING', 'BUILDING', 'READY', 'FAILED']),
        counts: SelectionCounts.nullable(),
        selectionAsOf: IsoDateTime.nullable(),
        createdAtLte: IsoDateTime.nullable(),
        expiresAt: IsoDateTime.nullable(),
    })
        .nullable(),
    reviewProfile: z
        .object({ id: Uuid, version: z.number().int(), name: z.string(), correctness: ModelRef, errorRisk: ModelRef })
        .nullable(),
    progress: BatchProgress,
    results: ResultCounts,
    /** Tokens je bestätigt gelöstem Ticket; null = „–“ (keine Erfolge). */
    tokensPerResolved: z.number().nullable(),
    reusedTokens: z.number().int(),
    executionRuns: z.array(z.lazy(() => ExecutionRunSummary)),
    budget: z.object({ maxTokens: z.number().int().nullable(), maxRuntimeMinutes: z.number().int().nullable() }),
    stateReason: z.string().nullable(),
    contentExpired: z.boolean(),
    /** Ergänzung Backend: Scoreverteilungen (Klassen 0–9 … 90–100, Index 10 = ohne gültige Bewertung). */
    distributions: z.object({ correctness: z.array(z.number().int()).length(11), errorRisk: z.array(z.number().int()).length(11) }).optional(),
});
export const PreheatRequest = z.object({
    expectedRowVersion: z.number().int(),
    generatorModelConfigId: Uuid,
    /** Ergänzung Backend: optional abweichendes aktives Prüfprofil (Default: administriertes). */
    reviewProfileId: Uuid.nullable().optional(),
});
export const StopRequest = z.object({ expectedRowVersion: z.number().int() });
export const BatchTicketsQuery = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    filter: z.enum(['all', 'eligible', 'blocked', 'failed', 'excluded', 'needs_review']).default('all'),
    sort: z.enum(['nummer', 'correctness', 'errorRisk', 'gesellschaft', 'status']).default('nummer'),
    dir: z.enum(['asc', 'desc']).default('asc'),
    minCorrectness: z.coerce.number().int().min(0).max(100).optional(),
    maxErrorRisk: z.coerce.number().int().min(0).max(100).optional(),
    /** Ergänzung Backend: Filter nach primärer Ergebniskategorie. */
    resultCategory: ResultCategory.optional(),
});
export const EvaluationView = z.object({
    id: Uuid,
    role: z.enum(['CORRECTNESS', 'ERROR_RISK']),
    status: z.enum(['PENDING', 'VALID', 'FAILED', 'STALE']),
    score: Score.nullable(),
    reason: z.string().nullable(),
    blockers: z.array(z.string()),
    details: z.record(z.string(), z.unknown()),
    model: ModelRef.nullable(),
    proposalVersion: z.number().int(),
    createdAt: IsoDateTime,
});
export const BatchTicketRow = z.object({
    id: Uuid,
    nidusTicketId: Uuid,
    nummer: z.string(),
    betreff: z.string().nullable(),
    gesellschaftId: Uuid,
    gesellschaftName: z.string().nullable(),
    objektId: Uuid.nullable(),
    objektBezeichnung: z.string().nullable(),
    vorgangstypId: Uuid.nullable(),
    vorgangstypName: z.string().nullable(),
    status: VorgangStatus,
    bisherigerBearbeiter: z.string().nullable(),
    analysisState: AnalysisState,
    proposalVersion: z.number().int().nullable(),
    resolutionKind: ResolutionKind.nullable(),
    correctness: Score.nullable(),
    errorRisk: Score.nullable(),
    /** false, wenn Scores veraltet sind (z. B. nach Textänderung). */
    scoresCurrent: z.boolean(),
    blockers: z.array(z.string()),
    excluded: z.boolean(),
    eligible: z.boolean(),
    ineligibleReason: z.string().nullable(),
    resultCategory: ResultCategory,
    /** Ergänzung Backend: zuletzt bekannter Nidus-Status (Kontextabruf/Operationsergebnis); `status` bleibt der Auswahlstand. */
    currentStatus: VorgangStatus.nullable().optional(),
    executionState: ExecutionItemState.nullable(),
    reused: z.boolean(),
    claim: z.object({ active: z.boolean(), mine: z.boolean(), until: IsoDateTime.nullable() }),
    rowVersion: z.number().int(),
});
export const ProposalVersionView = z.object({
    version: z.number().int(),
    text: z.string().nullable(),
    sha256: z.string(),
    author: z.object({ kind: z.enum(['MODEL', 'USER']), name: z.string() }),
    resolutionKind: ResolutionKind.nullable(),
    claims: z.array(z.object({ text: z.string(), sourceIds: z.array(z.string()) })),
    missingInformation: z.array(z.string()),
    blockers: z.array(z.string()),
    createdAt: IsoDateTime,
});
export const BatchTicketDetail = BatchTicketRow.extend({
    nidusUrl: z.string().nullable(),
    context: z
        .object({
        completeness: Completeness,
        issues: z.array(ContextIssue),
        sources: z.array(Source),
        exclusionHits: z.array(ExclusionHit),
        expired: z.boolean(),
    })
        .nullable(),
    proposals: z.array(ProposalVersionView),
    evaluations: z.array(EvaluationView),
    readOnly: z.boolean(),
    readOnlyReason: z.string().nullable(),
    execution: z
        .object({
        state: ExecutionItemState,
        agentDisplayName: z.string().nullable(),
        nidusOperationId: Uuid.nullable(),
        errorCode: z.string().nullable(),
    })
        .nullable(),
});
export const UpdateProposalRequest = z.object({
    expectedVersion: z.number().int(),
    text: z.string().min(1).max(20000),
});
export const EvaluateRequest = z.object({ expectedVersion: z.number().int() });
export const ExclusionRequest = z.object({ excluded: z.boolean(), expectedRowVersion: z.number().int() });
export const EligibilityResponse = z.object({
    minCorrectness: Score,
    maxErrorRisk: Score,
    eligibleCount: z.number().int(),
    total: z.number().int(),
    /** Gründe für nicht ausführbare Tickets, disjunkt nach Priorität. */
    blockedByReason: z.record(z.string(), z.number().int()),
    batchRowVersion: z.number().int(),
});
// ---------------------------------------------------------------------------
// Ausführung
// ---------------------------------------------------------------------------
export const CreateExecutionRunRequest = z.object({
    expectedRowVersion: z.number().int(),
    minCorrectness: Score,
    maxErrorRisk: Score,
    /** Vom Nutzer gesehene Zahl; weicht die Serverberechnung ab → 409 mit neuen Ergebnissen. */
    expectedEligibleCount: z.number().int(),
});
export const ExecutionRunSummary = z.object({
    id: Uuid,
    batchId: Uuid,
    authorizationType: AuthorizationType,
    approver: z.object({ id: Uuid, name: z.string() }).nullable(),
    policyVersion: z.number().int().nullable(),
    minCorrectness: Score,
    maxErrorRisk: Score,
    state: z.enum(['RUNNING', 'STOPPING', 'COMPLETED', 'NEEDS_ATTENTION', 'CANCELLED']),
    counts: z.record(ExecutionItemState, z.number().int()),
    createdAt: IsoDateTime,
    finishedAt: IsoDateTime.nullable(),
});
// ---------------------------------------------------------------------------
// Claims, Holds, Folgebatch (08 §5)
// ---------------------------------------------------------------------------
export const ClaimView = z.object({ batchTicketId: Uuid, until: IsoDateTime, fencingToken: z.number().int() });
export const CreateHoldRequest = z.object({ reason: z.string().min(1).max(500) });
export const HoldView = z.object({
    id: Uuid,
    nidusTicketId: Uuid,
    kind: z.enum(['OPERATOR_DEFERRAL', 'DELIVERY_UNKNOWN', 'SENT_NOT_RESOLVED', 'DELIVERY_FAILED']),
    reason: z.string(),
    contentRevision: z.string().nullable(),
    createdBy: z.string(),
    createdAt: IsoDateTime,
    releasedAt: IsoDateTime.nullable(),
});
export const FollowupBatchRequest = z.object({ batchTicketIds: z.array(Uuid).min(1).max(500) });
// ---------------------------------------------------------------------------
// Audit
// ---------------------------------------------------------------------------
export const AuditEventView = z.object({
    id: Uuid,
    at: IsoDateTime,
    actorType: z.enum(['USER', 'SYSTEM', 'SERVICE']),
    actorName: z.string().nullable(),
    action: z.string(),
    batchId: Uuid.nullable(),
    nidusTicketId: Uuid.nullable(),
    gesellschaftId: Uuid.nullable(),
    data: z.record(z.string(), z.unknown()),
});
// ---------------------------------------------------------------------------
// Administration
// ---------------------------------------------------------------------------
export const AgentProfileView = z.object({
    id: Uuid,
    displayName: z.string(),
    signature: z.string().nullable(),
    active: z.boolean(),
    gesellschaftIds: z.array(Uuid),
    nidusProfilId: Uuid.nullable(),
    syncState: z.enum(['PENDING', 'SYNCED', 'FAILED']),
    syncError: z.string().nullable(),
    rowVersion: z.number().int(),
});
export const CreateAgentProfileRequest = z.object({
    displayName: z.string().min(2).max(120),
    signature: z.string().max(2000).nullable(),
    gesellschaftIds: z.array(Uuid).min(1),
});
export const UpdateAgentProfileRequest = CreateAgentProfileRequest.partial().extend({
    expectedRowVersion: z.number().int(),
    active: z.boolean().optional(),
});
export const Provider = z.enum(['OPENAI', 'ANTHROPIC', 'AZURE_OPENAI', 'MOCK']);
export const ModelRoleSupport = z.enum(['GENERATOR', 'CORRECTNESS', 'ERROR_RISK']);
export const ModelConfigView = z.object({
    id: Uuid,
    name: z.string(),
    provider: Provider,
    modelId: z.string(),
    endpoint: z.string().nullable(),
    roles: z.array(ModelRoleSupport),
    contextLimitTokens: z.number().int(),
    inputBudgetTokens: z.number().int(),
    outputLimitTokens: z.number().int(),
    timeoutSeconds: z.number().int(),
    active: z.boolean(),
    /** Nur ob ein Schlüssel hinterlegt ist + maskierte Endung; niemals Klartext. */
    secret: z.object({ configured: z.boolean(), masked: z.string().nullable(), updatedAt: IsoDateTime.nullable() }),
    version: z.number().int(),
    rowVersion: z.number().int(),
});
export const UpsertModelConfigRequest = z.object({
    name: z.string().min(1).max(80),
    provider: Provider,
    modelId: z.string().min(1).max(120),
    endpoint: z.string().url().nullable(),
    roles: z.array(ModelRoleSupport).min(1),
    contextLimitTokens: z.number().int().positive(),
    inputBudgetTokens: z.number().int().positive(),
    outputLimitTokens: z.number().int().positive(),
    timeoutSeconds: z.number().int().min(5).max(600),
    active: z.boolean(),
    expectedRowVersion: z.number().int().optional(),
});
export const SetSecretRequest = z.object({ secret: z.string().min(8).max(500) });
export const ConnectionTestResult = z.object({ ok: z.boolean(), resolvedModelId: z.string().nullable(), message: z.string() });
export const ReviewProfileView = z.object({
    id: Uuid,
    name: z.string(),
    version: z.number().int(),
    correctnessModelConfigId: Uuid,
    errorRiskModelConfigId: Uuid,
    promptVersions: z.object({ generator: z.string(), correctness: z.string(), errorRisk: z.string() }),
    active: z.boolean(),
    createdAt: IsoDateTime,
});
export const UpsertReviewProfileRequest = z.object({
    name: z.string().min(1).max(80),
    correctnessModelConfigId: Uuid,
    errorRiskModelConfigId: Uuid,
    active: z.boolean(),
});
export const ExclusionTermView = z.object({
    id: Uuid,
    input: z.string(),
    normalized: z.string(),
    active: z.boolean(),
    createdBy: z.string(),
    createdAt: IsoDateTime,
});
export const ExclusionListView = z.object({
    version: z.number().int(),
    syncState: z.enum(['PENDING', 'SYNCED', 'FAILED']),
    terms: z.array(ExclusionTermView),
});
export const AddExclusionTermRequest = z.object({ input: z.string().min(1).max(200), expectedVersion: z.number().int() });
export const ExclusionProbeRequest = z.object({ text: z.string().max(50000) });
export const ExclusionProbeResponse = z.object({ hits: z.array(z.object({ term: z.string(), index: z.number().int() })) });
export const OperationsView = z.object({
    installation: z.object({ name: z.string(), nidusInstanceId: z.string(), nidusBaseUrl: z.string() }),
    handshake: z.object({ ok: z.boolean(), apiVersion: z.string().nullable(), checkedAt: IsoDateTime.nullable(), message: z.string().nullable() }),
    executionGate: z.object({
        desiredEnabled: z.boolean(),
        confirmed: z.boolean(),
        version: z.number().int(),
        envEnabled: z.boolean(),
    }),
    queue: z.object({ queued: z.number().int(), running: z.number().int(), dead: z.number().int(), oldestQueuedAgeSeconds: z.number().nullable() }),
    unknownDeliveries: z.number().int(),
});
export const SetExecutionGateRequest = z.object({ enabled: z.boolean(), expectedVersion: z.number().int() });
export const UserView = z.object({
    id: Uuid,
    name: z.string(),
    email: z.string().nullable(),
    roles: z.array(Role),
    gesellschaftIds: z.array(Uuid),
    lastSeenAt: IsoDateTime.nullable(),
    /** Ergänzung Backend: für SetUserScopeRequest.expectedRowVersion. */
    rowVersion: z.number().int().optional(),
});
export const SetUserScopeRequest = z.object({ gesellschaftIds: z.array(Uuid), expectedRowVersion: z.number().int() });
// ---------------------------------------------------------------------------
// Gesellschaftsautomatik (08)
// ---------------------------------------------------------------------------
export const EffectiveState = z.enum(['DISABLED', 'SYNCING', 'ENABLED', 'DISABLING', 'BLOCKED']);
export const Interval = z
    .number()
    .int()
    .min(INTERVAL_MIN)
    .max(INTERVAL_MAX)
    .refine((v) => v % INTERVAL_STEP === 0, { message: `Intervall in Schritten von ${INTERVAL_STEP} Minuten` });
export const CompanyAutomationView = z.object({
    gesellschaftId: Uuid,
    gesellschaftName: z.string(),
    desiredEnabled: z.boolean(),
    effectiveState: EffectiveState,
    blockedReason: z.string().nullable(),
    generator: ModelRef.nullable(),
    reviewProfile: z.object({ id: Uuid, version: z.number().int(), name: z.string() }).nullable(),
    minCorrectness: Score,
    maxErrorRisk: Score,
    intervalMinutes: Interval,
    minTicketAgeMinutes: z.literal(30),
    policyVersion: z.number().int(),
    scheduleGeneration: z.number().int(),
    nextRunAt: IsoDateTime.nullable(),
    lastRun: z
        .object({ scheduledFor: IsoDateTime, status: z.string(), batchId: Uuid.nullable(), resolved: z.number().int() })
        .nullable(),
    configuredBy: z.string().nullable(),
    configuredAt: IsoDateTime.nullable(),
    syncState: z.enum(['PENDING', 'SYNCED', 'FAILED']),
    backlog: z.object({
        count: z.number().int().nullable(),
        asOf: IsoDateTime.nullable(),
        state: z.enum(['FRESH', 'STALE', 'UNAVAILABLE']),
    }),
    csat: z.object({ availability: z.literal('NOT_IMPLEMENTED'), value: z.null() }),
});
export const UpdateCompanyAutomationRequest = z.object({
    expectedVersion: z.number().int(),
    desiredEnabled: z.boolean(),
    generatorModelConfigId: Uuid.nullable(),
    reviewProfileId: Uuid.nullable(),
    minCorrectness: Score,
    maxErrorRisk: Score,
    intervalMinutes: Interval,
});
export const ScheduleRunView = z.object({
    id: Uuid,
    scheduledFor: IsoDateTime,
    policyVersion: z.number().int(),
    scheduleGeneration: z.number().int(),
    status: z.enum(['CLAIMED', 'RUNNING', 'COMPLETED', 'EMPTY_SELECTION', 'NO_ELIGIBLE_TICKETS', 'SKIPPED_OVERLAP', 'MISSED', 'FAILED', 'CANCELLED']),
    missedSlotCount: z.number().int(),
    batchId: Uuid.nullable(),
    startedAt: IsoDateTime.nullable(),
    finishedAt: IsoDateTime.nullable(),
});
// ---------------------------------------------------------------------------
// Ergänzungen Backend (23.09.2026, additiv; siehe docs/contract-changes.md)
// ---------------------------------------------------------------------------
export const HealthResponse = z.object({
    status: z.enum(['ok', 'degraded']),
    version: z.string(),
    nidus: z.object({ instanceId: z.string(), handshakeOk: z.boolean() }),
    authMode: z.enum(['entra', 'dev']),
});
/** Nur AUTH_MODE=dev (nie in production): Token für einen Dev-Nutzer (docs/dev-auth.md). */
export const DevTokenRequest = z.object({ email: z.string().min(3).max(200) });
export const DevTokenResponse = z.object({ accessToken: z.string(), token: z.string(), expiresIn: z.number().int() });
export const BatchListQuery = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    trigger: Trigger.optional(),
    phase: BatchPhase.optional(),
    gesellschaftId: Uuid.optional(),
});
/** Fortsetzen eines pausierten Batches; Budgetänderung nur Admin (03 „Budgetlimit“). */
export const ResumeBatchRequest = z.object({
    expectedRowVersion: z.number().int(),
    budget: z.object({ maxTokens: z.number().int().positive().nullable().optional(), maxRuntimeMinutes: z.number().int().positive().nullable().optional() }).optional(),
});
/** Stop eines Ausführungslaufs; expectedRowVersion optional (Web sendet `{}`). */
export const RunStopRequest = z.object({ expectedRowVersion: z.number().int().optional() });
export const AuditQuery = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    batchId: Uuid.optional(),
    nidusTicketId: Uuid.optional(),
    action: z.string().max(100).optional(),
    actorType: z.enum(['USER', 'SYSTEM', 'SERVICE']).optional(),
    from: IsoDateTime.optional(),
    to: IsoDateTime.optional(),
});
export const CompanyListQuery = z.object({ cursor: z.string().optional(), limit: z.coerce.number().int().min(1).max(200).default(50) });
export const ScheduleRunListQuery = CompanyListQuery;
export const ExclusionRemoveQuery = z.object({ expectedVersion: z.coerce.number().int() });
export const ReleaseHoldQuery = z.object({ note: z.string().max(500).optional() });
//# sourceMappingURL=api.js.map